import { SantaGiftsDashboard } from './SantaGiftsDashboard';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Select, Hint, showDialog } from '@src/components';
import { CopyValue } from './AdminPayments';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import './AdminPayments.css';
import './AdminSantaGifts.css';

type GiftBox = {
  id: number;
  name: string;
  drawConducted: boolean;
  participantCount: number;
  recipientUserIds?: number[];
  organizers: { id: number; email: string | null }[];
  organizerCount: number;
  returningOrganizerCount: number;
  priorOrganizerYears: string[];
  createdAt: string;
  recentParticipantCount: number;
  lastParticipantActiveAt: string | null;
};
type GiftRecipient = { id: number; username: string; email: string | null; cardNames: string[]; seenAt: string | null; thankedAt: string | null; thankYouMessage: string | null; thankAnonymously: boolean | null; giftedBoxCount: number };
type Gift = GiftBox & { gifter: { id: number; email: string | null; cardName: string | null; picture: string | null; avatar: string | null; frameName: string | null; giftedBoxCount: number } | null; giftAnonymously: boolean; message: string | null; recipients: GiftRecipient[]; giftId: number; status: 'gifted' | 'delivered'; giftedAt: string };
type History = { gifts: Gift[]; total: number; page: number; pageSize: number };

export function AdminSantaGifts({ onDenied }: { onDenied: () => void }) {
  const { t, i18n } = useTranslation();
  const [candidates, setCandidates] = useState<GiftBox[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<'generate' | 'search' | 'send' | null>(null);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState<{ sent: number; skipped: number } | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [page, setPage] = useState(1);
  const [candidateLimit, setCandidateLimit] = useState('10');
  const [passedOnFilter, setPassedOnFilter] = useState('all');
  const [senderFilter, setSenderFilter] = useState('all');
  const [candidateBoxId, setCandidateBoxId] = useState('');
  const [searchedCandidate, setSearchedCandidate] = useState(false);
  const [boxSearch, setBoxSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [revision, setRevision] = useState(0);
  const [historyRequest, setHistoryRequest] = useState('');
  const historyLoading = historyRequest !== `${page}:${revision}:${boxSearch}:${senderFilter}:${passedOnFilter}`;
  const selectedUserCount = new Set(
    (candidates || []).filter(box => selected.has(box.id)).flatMap(box => box.recipientUserIds || []),
  ).size;
  const date = (value: string | null) =>
    value
      ? new Date(value).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
      : t('admin.gifts.unknown');

  useEffect(() => {
    const controller = new AbortController();
    void fetch(`/api/admin/santa-gifts?page=${page}&boxSearch=${encodeURIComponent(boxSearch)}&sender=${senderFilter}&passedOn=${passedOnFilter}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (response) => {
        if ([401, 403].includes(response.status)) {
          onDenied();
          return;
        }
        if (!response.ok) throw new Error('History unavailable');
        const data: History = await response.json();
        if (!controller.signal.aborted) {
          setHistoryError(false);
          setHistory(data);
          setPage(data.page);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setHistoryError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setHistoryRequest(`${page}:${revision}:${boxSearch}:${senderFilter}:${passedOnFilter}`);
      });
    return () => controller.abort();
    // The parent callback only changes authorization; inline identity must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, revision, boxSearch, senderFilter, passedOnFilter]);

  async function generate(boxId?: string) {
    if (boxId !== undefined && (!/^[1-9][0-9]*$/.test(boxId) || !Number.isSafeInteger(Number(boxId)) || Number(boxId) > 2147483647)) {
      setError('invalidCandidateId');return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(boxId === undefined ? 'generate' : 'search');
    setSearchedCandidate(boxId !== undefined);
    setError('');
    setOutcome(null);
    setSelected(new Set());
    setCandidates(null);
    try {
      const response = await fetch(`/api/admin/santa-gifts/candidates?limit=${candidateLimit}${boxId === undefined ? '' : `&boxId=${encodeURIComponent(boxId)}`}`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if ([401, 403].includes(response.status)) {
        onDenied();
        return;
      }
      if (!response.ok) throw new Error('Candidates unavailable');
      const data: { boxes: GiftBox[] } = await response.json();
      setCandidates(data.boxes);
    } catch {
      setError('generateError');
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  async function send() {
    if (busyRef.current || !selected.size) return;
    busyRef.current = true;
    const boxIds = [...selected];
    setBusy('send');
    setError('');
    setOutcome(null);
    try {
      const response = await fetch('/api/admin/santa-gifts/send', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boxIds, explicitBox: searchedCandidate }),
      });
      if ([401, 403].includes(response.status)) {
        onDenied();
        return;
      }
      if (!response.ok) throw new Error('Send failed');
      const data: { gifts: { id: number; boxId: number }[]; skippedBoxIds: number[] } =
        await response.json();
      setOutcome({ sent: data.gifts.length, skipped: data.skippedBoxIds.length });
      setCandidates((rows) => rows?.filter((row) => !boxIds.includes(row.id)) ?? null);
      setSelected(new Set());
      setPage(1);
      setRevision((value) => value + 1);
    } catch {
      setError('sendError');
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  function showRecipients(gift: Gift) {
    const recipients = [...(gift.recipients ?? [])].sort((a, b) => Number(gift.organizers?.some(org => org.id === b.id)) - Number(gift.organizers?.some(org => org.id === a.id)));
    showDialog({ title: t('admin.gifts.recipientsFor', { name: gift.name }), mobileFullScreen: true,
      testId: 'admin-gift-recipients-dialog',
      children: <div className="admin-payments admin-gift-recipients">
        <div className="admin-payments__scroll" tabIndex={0} role="region" aria-label={t('admin.gifts.recipients')}>
          <table><thead><tr>
            {['recipient', 'viewed', 'thankMessage', 'passedOn'].map(key => <th scope="col" key={key}>{t(`admin.gifts.${key}`)}</th>)}
          </tr></thead><tbody>{recipients.map(recipient => <tr key={recipient.id}>
            <td><div className="admin-gifts__contact">
              <CopyValue value={String(recipient.id)} label={t('admin.gifts.copyRecipientId', { id: recipient.id })} />
              <strong>{(Array.isArray(recipient.cardNames) ? recipient.cardNames.join(', ') : recipient.cardNames) || '—'}</strong>
              {gift.organizers?.some(org => org.id === recipient.id) && <span>{t('admin.gifts.organizerLabel')}</span>}
              {recipient.email ? <CopyValue value={recipient.email} label={t('admin.gifts.copyRecipientEmail', { email: recipient.email })} /> : <span>{t('admin.gifts.noEmail')}</span>}
            </div></td>
            <td>{recipient.seenAt ? <time dateTime={recipient.seenAt}>{date(recipient.seenAt)}</time> : t('admin.gifts.notViewed')}</td>
            <td>{recipient.thankedAt ? <><p className="admin-gift-recipients__message">{recipient.thankYouMessage || t('boxGift.emptyReply')}</p><time dateTime={recipient.thankedAt}>{date(recipient.thankedAt)}</time><div>{t(recipient.thankAnonymously ? 'admin.gifts.anonymousGift' : 'admin.gifts.identityShared')}</div></> : t('admin.gifts.noThank')}</td>
            <td>{recipient.giftedBoxCount > 0 ? t('admin.gifts.passedOnCount', { count: recipient.giftedBoxCount }) : t('admin.gifts.notPassedOn')}</td>
          </tr>)}</tbody></table>
        </div>
        {!recipients.length && <p>{t('admin.gifts.noRecipients')}</p>}
      </div>,
    });
  }

  function headers(showOrganizerContacts = true) {
    return (
      <>
        <th scope="col">{t('admin.gifts.box')}</th>
        {showOrganizerContacts && <th scope="col">{t('admin.gifts.drawStatus')}</th>}
        {showOrganizerContacts && <th scope="col">{t('admin.gifts.organizerContacts')}</th>}
        <th scope="col">{t('admin.gifts.organizers')}</th>
        <th scope="col">{t('admin.gifts.created')}</th>
        <th scope="col">{t('admin.gifts.activity')}</th>
      </>
    );
  }
  function cells(box: GiftBox, showOrganizerContacts = true) {
    return (
      <>
        <td className="admin-gifts__name">
          <div className="admin-gifts__contact">
            <CopyValue value={String(box.id)} label={t('admin.gifts.copyId', { id: box.id })} />
            <span>{box.name}</span>
            <span>{t('admin.gifts.participantTotal', { count: box.participantCount })}</span>
          </div>
        </td>
        {showOrganizerContacts && <td>{t(box.drawConducted ? 'admin.gifts.drawConducted' : 'admin.gifts.drawNotConducted')}</td>}
        {showOrganizerContacts && <td className="admin-gifts__contacts">
          {box.organizers?.length
            ? box.organizers.map((organizer) => (
                <div key={organizer.id} className="admin-gifts__contact">
                  <CopyValue
                    value={String(organizer.id)}
                    label={t('admin.gifts.copyOrganizerId', { id: organizer.id })}
                  />
                  {organizer.email ? (
                    <CopyValue
                      value={organizer.email}
                      label={t('admin.gifts.copyOrganizerEmail', { email: organizer.email })}
                    />
                  ) : (
                    <span>{t('admin.gifts.noEmail')}</span>
                  )}
                </div>
              ))
            : t('admin.gifts.noOrganizers')}
        </td>}
        <td className="admin-gifts__organizers">
          {box.organizerCount === 0 ? (
            t('admin.gifts.noOrganizers')
          ) : box.returningOrganizerCount > 0 ? (
            <>
              {t('admin.gifts.returning', {
                returning: box.returningOrganizerCount,
                total: box.organizerCount,
              })}
              <small>{box.priorOrganizerYears.join(', ')}</small>
            </>
          ) : (
            t('admin.gifts.firstSeason')
          )}
        </td>
        <td>
          <time dateTime={box.createdAt}>{date(box.createdAt)}</time>
        </td>
        <td>
          <div className="admin-gifts__contact">
          <span>{t('admin.gifts.recentTotal', { count: box.recentParticipantCount })}</span>
          <span>{t('admin.gifts.lastActive')}: {box.lastParticipantActiveAt ? (
            <time dateTime={box.lastParticipantActiveAt}>{date(box.lastParticipantActiveAt)}</time>
          ) : (
            date(null)
          )}</span>
          </div>
        </td>
      </>
    );
  }
  return (
    <div className="admin-payments admin-gifts" data-testid="admin-santa-gifts">
      <SantaGiftsDashboard revision={revision} onDenied={onDenied} />
      <section aria-labelledby="gift-candidates-title">
        <h2 id="gift-candidates-title">{t('admin.gifts.candidates')}</h2>
        <p className="admin-gifts__description">{t('admin.gifts.criteria', { count: Number(candidateLimit) })}</p>
        <p className="admin-gifts__description">{t('admin.gifts.activityNote')}</p>
        <div className="admin-gifts__candidate-controls">
          <form className="admin-gifts__candidate-search" onSubmit={event => { event.preventDefault(); void generate(candidateBoxId.trim()); }}>
            <input className="admin-gifts__search" inputMode="numeric" maxLength={10} value={candidateBoxId} onChange={event => setCandidateBoxId(event.target.value)} disabled={busy !== null} aria-label={t('admin.gifts.candidateBoxId')} placeholder={t('admin.gifts.candidateBoxId')} />
            <Button type="submit" variant="secondary" loading={busy === 'search'} disabled={busy !== null || !candidateBoxId.trim()}>{t('admin.gifts.findBox')}</Button>
          </form>
          <Select value={candidateLimit} onValueChange={setCandidateLimit}
            aria-label={t('admin.gifts.candidateLimit')} disabled={busy !== null}
            options={[5, 10, 15, 50].map(count => ({ value: String(count), label: t('admin.gifts.candidateCount', { count }) }))}
            testId="gift-candidate-limit" />
          <Button variant="secondary" loading={busy === 'generate'} disabled={busy !== null} onClick={() => void generate()}>
            {t('admin.gifts.generate')}
          </Button>
        </div>
        {error && <Hint tone="error">{t(`admin.gifts.${error}`)}</Hint>}
        {outcome && (
          <p role="status">
            {t('admin.gifts.sent', { count: outcome.sent })}
            {outcome.skipped > 0 && <> {t('admin.gifts.skipped', { count: outcome.skipped })}</>}
          </p>
        )}
        {candidates === null && busy === null && (
          <p className="admin-gifts__empty">{t('admin.gifts.start')}</p>
        )}
        {candidates !== null && candidates.length === 0 && (
          <p className="admin-gifts__empty">{t(searchedCandidate ? 'admin.gifts.noCandidateById' : 'admin.gifts.noCandidates')}</p>
        )}
        {!!candidates?.length && <div className="admin-gifts__selection-actions">
          <span>{t('admin.gifts.selected', { count: selected.size })}</span>
          <Button loading={busy === 'send'} disabled={busy !== null || !selected.size} onClick={() => void send()}>
            {t('admin.gifts.send', { count: selected.size, users: selectedUserCount })}
          </Button>
        </div>}
        {!!candidates?.length && (
          <div
            className="admin-payments__scroll"
            tabIndex={0}
            role="region"
            aria-label={t('admin.gifts.candidates')}
          >
            <table className="admin-gifts__table" data-testid="gift-candidates-table">
              <caption>{t('admin.gifts.candidateCount', { count: candidates.length })}</caption>
              <thead>
                <tr>
                  <th scope="col">
                    <input
                      type="checkbox"
                      aria-label={t('admin.gifts.selectAll')}
                      disabled={busy !== null}
                      checked={selected.size === candidates.length}
                      ref={(node) => {
                        if (node)
                          node.indeterminate =
                            selected.size > 0 && selected.size < candidates.length;
                      }}
                      onChange={(event) =>
                        setSelected(
                          new Set(event.target.checked ? candidates.map((row) => row.id) : []),
                        )
                      }
                    />
                  </th>
                  {headers()}
                </tr>
              </thead>
              <tbody>
                {candidates.map((box) => (
                  <tr key={box.id} data-selected={selected.has(box.id)}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={t('admin.gifts.selectBox', { name: box.name, id: box.id })}
                        disabled={busy !== null}
                        checked={selected.has(box.id)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setSelected((previous) => {
                            const next = new Set(previous);
                            if (checked) next.add(box.id);
                            else next.delete(box.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    {cells(box)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section aria-labelledby="gift-history-title" aria-busy={historyLoading}>
        <div className="admin-gifts__toolbar">
          <h2 id="gift-history-title">
            {t('admin.gifts.history')}
            {history ? ` · ${history.total}` : ''}
          </h2>
          <Button
            size="small"
            variant="ghost"
            disabled={historyLoading}
            onClick={() => setRevision((value) => value + 1)}
          >
            {t('admin.gifts.refresh')}
          </Button>
        </div>
        <form className="admin-gifts__toolbar" onSubmit={event => { event.preventDefault(); setPage(1); setBoxSearch(searchDraft.trim()); }}>
          <Select value={passedOnFilter} onValueChange={value => { setPassedOnFilter(value); setPage(1); }}
            aria-label={t('admin.gifts.passedOnFilter')}
            options={[{ value: 'all', label: t('admin.gifts.allPassOn') }, { value: 'yes', label: t('admin.gifts.onlyPassedOn') }]}
            testId="gift-passed-on-filter" />
          <Select value={senderFilter} onValueChange={value => { setSenderFilter(value); setPage(1); }}
            aria-label={t('admin.gifts.senderFilter')}
            options={[{ value: 'all', label: t('admin.gifts.allSenders') }, { value: 'others', label: t('admin.gifts.otherSenders') }]}
            testId="gift-sender-filter" />
          <input className="admin-gifts__search" type="search" maxLength={200} value={searchDraft} onChange={event => setSearchDraft(event.target.value)} aria-label={t('admin.gifts.boxFilter')} placeholder={t('admin.gifts.boxFilter')} />
          <Button type="submit" size="small" variant="secondary">{t('admin.gifts.filter')}</Button>
          {(boxSearch || searchDraft) && <Button type="button" size="small" variant="ghost" onClick={() => { setSearchDraft(''); setBoxSearch(''); setPage(1); }}>{t('admin.gifts.clearFilter')}</Button>}
        </form>
        {historyLoading ? (
          <Spinner label={t('account.loading')} />
        ) : historyError ? (
          <Hint tone="error">{t('admin.gifts.historyError')}</Hint>
        ) : (
          history && (
            <>
              {!history.gifts.length ? (
                <p className="admin-gifts__empty">{t('admin.gifts.noHistory')}</p>
              ) : (
                <div
                  className="admin-payments__scroll"
                  tabIndex={0}
                  role="region"
                  aria-label={t('admin.gifts.history')}
                >
                  <table
                    className="admin-gifts__table admin-gifts__table--history"
                    data-testid="gift-history-table"
                  >
                    <thead>
                      <tr>
                        <th scope="col">{t('admin.gifts.giftId')}</th>
                        <th scope="col">{t('admin.gifts.gifter')}</th>
                        <th scope="col">{t('admin.gifts.message')}</th>
                        {headers(false)}
                        <th scope="col">{t('admin.gifts.recipients')}</th>
                        <th scope="col">{t('admin.gifts.sentAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.gifts.map((gift) => (
                        <tr key={gift.giftId}>
                          <td>{gift.giftId}</td>
                          <td className="admin-gifts__contacts">
                            {gift.gifter ? <div className="admin-gifts__giver">
                              <span className="admin-gifts__giver-avatar"><ParticipantFace picture={gift.gifter.picture || 'unknown'} card={gift.gifter} index={gift.gifter.id} /></span>
                              <div className="admin-gifts__contact">
                              <div className="admin-gifts__giver-heading">
                                <CopyValue value={String(gift.gifter.id)} label={t('admin.gifts.copyGifterId', { id: gift.gifter.id })} />
                                {gift.gifter.cardName && <strong>{gift.gifter.cardName}</strong>}
                              </div>
                              {gift.gifter.email ? <CopyValue value={gift.gifter.email} label={t('admin.gifts.copyGifterEmail', { email: gift.gifter.email })} /> : <span>{t('admin.gifts.noEmail')}</span>}
                              <span>{t(gift.giftAnonymously ? 'admin.gifts.anonymousGift' : 'admin.gifts.namedGift')}</span>
                              <span>{t('admin.gifts.gifterTotal', { count: gift.gifter.giftedBoxCount })}</span>
                            </div></div> : <span>{t('admin.gifts.noGifter')}</span>}
                          </td>
                          <td><div className="admin-gifts__message">{gift.message || t('admin.gifts.noMessage')}</div></td>
                          {cells(gift, false)}
                          <td>
                            <div className="admin-gifts__contact">
                              <span>{t('admin.gifts.viewedCount', { count: gift.recipients?.filter(recipient => recipient.seenAt).length ?? 0, total: gift.recipients?.length ?? 0 })}</span>
                              <span>{t('admin.gifts.thankedCount', { count: gift.recipients?.filter(recipient => recipient.thankedAt).length ?? 0, total: gift.recipients?.length ?? 0 })}</span>
                              <span>{t('admin.gifts.sentGiftsCount', { count: gift.recipients?.filter(recipient => recipient.giftedBoxCount > 0).length ?? 0, total: gift.recipients?.length ?? 0 })}</span>
                              <Button size="small" variant="secondary" onClick={() => showRecipients(gift)}>{t('admin.gifts.showRecipients')}</Button>
                            </div>
                          </td>
                          <td>
                            <time dateTime={gift.giftedAt}>{date(gift.giftedAt)}</time>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {history.total > history.pageSize && (
                <div className="admin-gifts__toolbar">
                  <Button
                    size="small"
                    variant="ghost"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {t('admin.gifts.previous')}
                  </Button>
                  <span>
                    {t('admin.gifts.page', {
                      page: history.page,
                      pages: Math.ceil(history.total / history.pageSize),
                    })}
                  </span>
                  <Button
                    size="small"
                    variant="ghost"
                    disabled={page * history.pageSize >= history.total}
                    onClick={() => setPage(page + 1)}
                  >
                    {t('admin.gifts.next')}
                  </Button>
                </div>
              )}
            </>
          )
        )}
      </section>
    </div>
  );
}
